import type { ActionCableManagerAPI } from 'components/AI/ActionCableManager'
import type { WebSocketContextValue } from 'contexts/WebSocketContext'

/**
 * WebSocket Context Adapter for Redux Thunks
 *
 * This provides a way for Redux thunks to access the WebSocket context
 * since thunks cannot directly use React Context hooks.
 *
 * The adapter is set up by the useAIChat hook and used by thunks.
 */
class WebSocketContextAdapter {
  private context: WebSocketContextValue | null = null

  /**
   * Set the WebSocket context (called by useAIChat hook)
   */
  setContext(context: WebSocketContextValue): void {
    this.context = context
  }

  /**
   * Get a manager by ID (compatible with webSocketRegistry.get)
   */
  get(id: string): ActionCableManagerAPI | undefined {
    if (!this.context) {
      // Context not initialized yet - this is expected during app startup
      return undefined
    }
    return this.context.getManager(id)
  }

  /**
   * Get the active manager (compatible with webSocketRegistry.getActive)
   */
  getActive(): ActionCableManagerAPI | undefined {
    if (!this.context) {
      // Context not initialized yet - this is expected during app startup
      return undefined
    }
    return this.context.getActiveManager()
  }

  /**
   * Get the active manager ID (compatible with webSocketRegistry.getActiveId)
   */
  getActiveId(): string | null {
    if (!this.context) {
      // Context not initialized yet - this is expected during app startup
      return null
    }
    return this.context.getActiveManagerId()
  }

  /**
   * Check if there's an active connection (compatible with webSocketRegistry.hasActiveConnection)
   */
  hasActiveConnection(): boolean {
    if (!this.context) {
      // Context not initialized yet - this is expected during app startup
      return false
    }
    return this.context.hasActiveConnection()
  }
}

/**
 * Global adapter instance for Redux thunks to use
 */
export const webSocketContextAdapter = new WebSocketContextAdapter()

/**
 * Legacy compatibility interface for Redux thunks
 */
export const webSocketRegistry = {
  get: (id: string) => webSocketContextAdapter.get(id),
  getActive: () => webSocketContextAdapter.getActive(),
  getActiveId: () => webSocketContextAdapter.getActiveId(),
  hasActiveConnection: () => webSocketContextAdapter.hasActiveConnection(),
}
